import React, { useContext } from 'react';

import { Box, Button, rem } from '@vimeo/bokeh';
import { getSatelliteCheckoutParamsFromCurrentLocation } from '@vimeo/picox';

import CTALink from './Links/CTALink';
import TrackingContext from './TrackingContext';
import BpTracking from '../../utils/bpTracking';
import { PLANS } from '../../utils/constants';
import { CtaTreatment, PlanData } from '../../utils/interface';
import translations from '../../utils/translations';
import {
  isSeatTierPlan,
  getPlanCTALink,
  appendExtraQueryParams,
  appendTrackingQueryParams,
  appendPaywallParams,
} from '../../utils/utils';

interface Props {
  ctaTreatment?: CtaTreatment;
  planData: PlanData;
  displayMonthlyPlan: boolean;
  submitButtonText?: string;
  buttonXPadding?: string;
  buttonYPadding?: string;
  buttonBottomSpacing?: string;
  buttonRadius?: string;
  border?: string;
  color?: string;
  backgroundColor?: string;
  whiteSpace?: string;
  allowFreeTrial?: boolean;
}

const CTAButton = ({
  ctaTreatment,
  planData,
  displayMonthlyPlan,
  submitButtonText,
  buttonRadius,
  buttonXPadding,
  buttonYPadding,
  buttonBottomSpacing,
  border,
  color,
  backgroundColor,
  whiteSpace,
  allowFreeTrial,
}: Props) => {
  const trackingProps = useContext(TrackingContext);

  let buttonText: string;
  let ctaButtonLink: string;
  let purchase_type: string;
  const isEnterprisePlan = planData.tier === PLANS.ENTERPRISE;

  if (!ctaTreatment) {
    ctaTreatment = allowFreeTrial ? 'purchase_secondary' : 'free_trial_secondary';
  }

  if (isEnterprisePlan) {
    buttonText = translations.contactSales;
    ctaButtonLink = '/enterprise/contact?mkc=ent-upgrade-plan-modal';
    purchase_type = 'direct';
  } else if (
    allowFreeTrial &&
    (ctaTreatment === 'free_trial_only' || ctaTreatment === 'purchase_secondary')
  ) {
    buttonText = translations.startFreeTrial;
    ctaButtonLink = planData.metadata.interactions.purchase.uri.freeTrial;
    purchase_type = 'trial';
  } else {
    buttonText = translations.upgrade;
    ctaButtonLink = getPlanCTALink(planData, displayMonthlyPlan, trackingProps.campaignOverride);
    purchase_type = 'direct';
  }

  if (submitButtonText) {
    buttonText = submitButtonText;
  }

  const handleClick = () => {
    BpTracking.trackButtonClick(
      {
        ...trackingProps.params,
        copy: buttonText,
        currency: planData.currency.currencyCode,
        device_type: trackingProps.device_type,
        duration: displayMonthlyPlan ? 'monthly' : 'annual',
        is_new_pricing: isSeatTierPlan(planData.tier),
        path: typeof window !== 'undefined' && window.location ? window.location.href : '',
        plan_selected: planData.tier,
        price: displayMonthlyPlan ? planData.price.monthly : planData.price.annualMonthly,
        purchase_type: purchase_type,
        target: isEnterprisePlan ? 'enterprise_contact_page' : 'checkout',
        target_path: ctaButtonLink,
      },
      trackingProps.bpEventVersion,
    );

    if (trackingProps.onButtonClick) {
      trackingProps.onButtonClick({
        copy: buttonText,
        duration: displayMonthlyPlan ? 'monthly' : 'annual',
        price: displayMonthlyPlan ? planData.price.monthly : planData.price.annualMonthly,
        currency: planData.currency.currencyCode,
        plan_selected: planData.tier,
        target_path: ctaButtonLink,
        free_trial: allowFreeTrial && !isEnterprisePlan,
      });
    }
  };

  const showSecondaryCTA =
    ctaTreatment === 'purchase_secondary' ||
    (ctaTreatment === 'free_trial_secondary' && allowFreeTrial);

  return (
    <Box w="100%" marginBottom={rem(8)}>
      <Button
        data-testid="upsell-cta"
        onClick={handleClick}
        as="a"
        target="_blank"
        w="100%"
        href={appendExtraQueryParams(
          appendPaywallParams(
            appendTrackingQueryParams(ctaButtonLink, trackingProps.params),
            trackingProps.paywallTracking,
          ),
          isEnterprisePlan ? undefined : getSatelliteCheckoutParamsFromCurrentLocation(),
        )}
        size="md"
        {...(!isEnterprisePlan && {
          _hover: {
            backgroundColor: 'vimeoBlue.300',
          },
        })}
        backgroundColor={
          backgroundColor ? backgroundColor : isEnterprisePlan ? 'text-primary' : 'fill-brand'
        }
        marginBottom={buttonBottomSpacing}
        paddingY={buttonYPadding}
        paddingX={buttonXPadding}
        borderRadius={buttonRadius}
        border={border}
        color={color}
        whiteSpace={whiteSpace}
      >
        {buttonText}
      </Button>
      {showSecondaryCTA && (
        <CTALink
          data-testid="upsell-secondary-cta"
          allowFreeTrial={allowFreeTrial}
          ctaTreatment={ctaTreatment}
          displayMonthlyPlan={displayMonthlyPlan}
          planData={planData}
        />
      )}
    </Box>
  );
};

export default CTAButton;
